import React, { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE = "https://rhoulamikhadija.com/qos_api";

// ─────────────────────────────────────────────────────────
// Design tokens — habillage institutionnel ANRT
// ─────────────────────────────────────────────────────────
const C = {
    navy: "#0B2545", navyMid: "#16294A", navyLight: "#2A3F5F",
    red: "#C81E3A", green: "#1B8A5A", amber: "#C9882B", orange: "#C8492F",
    paper: "#EEF1F6", card: "#FFFFFF", line: "#E1E7EF", lineSoft: "#EDF1F6",
    grey: "#64748B", greyLight: "#F6F8FB", muted: "#94A3B8", ink: "#0B2545",
};
const OP_COLOR = {
    "Opérateur 1": "#F36A21",
    "Opérateur 2": "#0066B3",
    "Opérateur 3": "#7A1FA2",
};
const opColor = (n) => OP_COLOR[n] || C.navy;
const SIGNE_COLOR = {
    "+++": "#0E6B3E", "++": "#1B8A5A", "+": "#4FA876",
    "=": C.amber, "-": "#D17A4A", "--": "#C8492F", "---": "#A11C2E",
};
const SIGNE_LABEL = {
    "+++": "Très supérieur", "++": "Supérieur", "+": "Légèrement supérieur",
    "=": "Dans la moyenne", "-": "Légèrement inférieur", "--": "Inférieur", "---": "Très inférieur",
};
const QA_COLOR = { parfaite: "#1B8A5A", acceptable: "#2D6CB3", mediocre: "#C9882B", mauvaise: "#A11C2E" };
const QA_TAG = { Parfaite: QA_COLOR.parfaite, Acceptable: QA_COLOR.acceptable, "Médiocre": QA_COLOR.mediocre, Mauvaise: QA_COLOR.mauvaise };
const FONT = "'IBM Plex Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const MONO = "'IBM Plex Mono', 'SFMono-Regular', Menlo, monospace";

const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (n) => nf.format(Number(n || 0));
const fmtPct = (n, d = 2) => `${Number(n || 0).toFixed(d).replace(".", ",")}\u00A0%`;

// ─────────────────────────────────────────────────────────
// Hooks API (contrat inchangé)
// ─────────────────────────────────────────────────────────
function useApi(path) {
    const [state, setState] = useState({ data: null, loading: true, error: null });
    useEffect(() => {
        let cancelled = false;
        setState(s => ({ ...s, loading: true, error: null }));
        fetch(`${API_BASE}${path}`)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(j => { if (!cancelled) setState({ data: j, loading: false, error: null }); })
            .catch(e => { if (!cancelled) setState({ data: null, loading: false, error: e.message }); });
        return () => { cancelled = true; };
    }, [path]);
    return state;
}

function useDynamic(path, filters) {
    const [state, setState] = useState({ data: null, loading: true, error: null });
    const key = JSON.stringify(filters);
    useEffect(() => {
        let cancelled = false;
        setState(s => ({ ...s, loading: true, error: null }));
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) p.append(k, v); });
        fetch(`${API_BASE}${path}?${p}`)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(j => { if (!cancelled) setState({ data: j, loading: false, error: null }); })
            .catch(e => { if (!cancelled) setState({ data: null, loading: false, error: e.message }); });
        return () => { cancelled = true; };
    }, [path, key]);
    return state;
}

function useMediaQuery(query) {
    const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
    useEffect(() => {
        const m = window.matchMedia(query);
        const h = () => setMatch(m.matches);
        m.addEventListener("change", h); h();
        return () => m.removeEventListener("change", h);
    }, [query]);
    return match;
}

// ─────────────────────────────────────────────────────────
// Primitives UI
// ─────────────────────────────────────────────────────────
function Card({ children, style }) {
    return <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: "0 1px 2px rgba(11,37,69,.04)", ...style }}>{children}</div>;
}

const Skeleton = ({ w = "100%", h = 14, r = 6, style }) => (
    <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg,#EEF1F6 25%,#E2E8F1 37%,#EEF1F6 63%)", backgroundSize: "400% 100%", animation: "qosShimmer 1.4s ease infinite", ...style }} />
);

function SkeletonTable({ rows = 4, cols = 6 }) {
    return (
        <div style={{ padding: "4px 0" }}>
            <Skeleton h={38} r={8} style={{ marginBottom: 10 }} />
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.lineSoft}` }}>
                    {Array.from({ length: cols }).map((__, j) => <Skeleton key={j} w={j === 0 ? "24%" : "12%"} h={13} />)}
                </div>
            ))}
        </div>
    );
}

const boxState = { padding: "40px 20px", textAlign: "center", color: C.grey, fontSize: 13.5, background: C.greyLight, border: `1px dashed ${C.line}`, borderRadius: 10, lineHeight: 1.6 };
const Empty = ({ msg }) => (
    <div style={boxState}>
        <div style={{ fontSize: 22, marginBottom: 8, opacity: .5 }}>◦</div>
        {msg || "Aucune donnée disponible."}
    </div>
);
const ApiError = ({ error }) => (
    <div style={{ padding: "14px 18px", background: "#FDECEF", border: `1px solid ${C.red}44`, borderRadius: 10, color: C.red, fontSize: 13, lineHeight: 1.6 }}>
        <strong>Service indisponible</strong> — impossible de joindre l'API ({error}). Vérifiez que le back-end répond sur <code style={{ fontFamily: MONO }}>{API_BASE}</code>.
    </div>
);

function SectionTitle({ children, sub, right }) {
    return (
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
                <div style={{ width: 3, height: sub ? 32 : 18, background: C.red, borderRadius: 2, marginTop: 1 }} />
                <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.navy }}>{children}</div>
                    {sub && <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, marginTop: 3 }}>{sub}</div>}
                </div>
            </div>
            {right}
        </div>
    );
}

function Section({ title, sub, right, children }) {
    return (
        <section style={{ marginBottom: 20 }}>
            <Card style={{ padding: "20px 22px" }}>
                <SectionTitle sub={sub} right={right}>{title}</SectionTitle>
                {children}
            </Card>
        </section>
    );
}

// Dropdown personnalisé
function DropSelect({ label, value, onChange, options, placeholder, minWidth = 150 }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const norm = options.map(o => (typeof o === "string" ? { value: o, label: o } : o));
    const selected = norm.find(o => o.value === value);
    const hasValue = !!value;
    return (
        <div ref={ref} style={{ position: "relative", userSelect: "none", flex: `1 1 ${minWidth}px`, minWidth, padding: "9px 16px", borderRight: `1px solid ${C.line}` }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.muted, marginBottom: 4 }}>{label}</div>
            <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, fontWeight: hasValue ? 600 : 500, color: hasValue ? C.navy : C.muted }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected ? selected.label : placeholder}</span>
                <span style={{ marginLeft: 8, color: C.muted, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", fontSize: 11 }}>▾</span>
            </div>
            {open && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 8, right: 8, zIndex: 200, background: C.card, border: `1px solid ${C.line}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(11,37,69,.14)", maxHeight: 260, overflowY: "auto" }}>
                    <div onClick={() => { onChange(""); setOpen(false); }} style={{ padding: "10px 14px", fontSize: 13, color: C.grey, cursor: "pointer", fontStyle: "italic", background: !value ? C.greyLight : "transparent", borderBottom: `1px solid ${C.line}` }}>{placeholder}</div>
                    {norm.map((o, i) => (
                        <div key={i} onClick={() => { onChange(o.value); setOpen(false); }} className="qos-opt" style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", color: C.navy, fontWeight: o.value === value ? 600 : 400, background: o.value === value ? "#EEF2F8" : "transparent" }}>{o.label}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Panneau de filtres unifié
// ─────────────────────────────────────────────────────────
const EMPTY_FILTERS = { ville: "", operateur: "", service: "", date_debut: "", date_fin: "", qa_categorie: "", campagne_selected: "" };
const labelStyle = { fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.muted };

function UnifiedFilterBar({ filters, setFilters, campagnes, villes, operateurs, services }) {
    const upd = (k) => (v) => setFilters(f => ({ ...f, [k]: v }));
    const onCampagne = (dateVal) => {
        if (!dateVal) { setFilters(f => ({ ...f, date_debut: "", date_fin: "", campagne_selected: "" })); return; }
        const p = dateVal.split("-");
        const iso = p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : dateVal;
        setFilters(f => ({ ...f, date_debut: iso, date_fin: iso, campagne_selected: dateVal }));
    };
    const campOpts = campagnes.map((c) => ({
        value: c.date_mesure,
        label: c.date_mesure,
    }));
    const chips = [];
    if (filters.campagne_selected) chips.push({ key: "campagne", tag: "Campagne", label: filters.campagne_selected, color: C.navy, clear: () => onCampagne("") });
    if (filters.ville) chips.push({ key: "ville", tag: "Ville", label: filters.ville, color: C.navy, clear: () => upd("ville")("") });
    if (filters.operateur) chips.push({ key: "operateur", tag: "Opérateur", label: filters.operateur, color: opColor(filters.operateur), clear: () => upd("operateur")("") });
    if (filters.service) chips.push({ key: "service", tag: "Techno", label: filters.service, color: "#2D6CB3", clear: () => upd("service")("") });
    if (filters.qa_categorie) chips.push({ key: "qa", tag: "Qualité", label: filters.qa_categorie, color: QA_TAG[filters.qa_categorie] || C.amber, clear: () => upd("qa_categorie")("") });
    if (filters.date_debut && !filters.campagne_selected) chips.push({ key: "periode", tag: "Période", label: `${filters.date_debut}${filters.date_fin && filters.date_fin !== filters.date_debut ? " → " + filters.date_fin : ""}`, color: C.navyLight, clear: () => setFilters(f => ({ ...f, date_debut: "", date_fin: "" })) });

    const n = chips.length;
    const summary = n ? `· ${n} filtre${n > 1 ? "s" : ""} actif${n > 1 ? "s" : ""}` : "· aucun filtre — vue nationale";
    return (
        <div style={{ marginBottom: 26 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.navy, borderLeft: `3px solid ${C.red}`, paddingLeft: 9 }}>Panneau de filtres</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.grey }}>{summary}</span>
                </div>
                {n > 0 && (
                    <button onClick={() => setFilters(EMPTY_FILTERS)} className="qos-btn-ghost" style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.03em", color: C.red, background: "transparent", border: `1px solid #E6B8C0`, borderRadius: 7, padding: "7px 13px", cursor: "pointer" }}>↺ RÉINITIALISER</button>
                )}
            </div>
            <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap", background: C.greyLight, border: `1px solid ${C.line}`, borderRadius: 10, padding: 4 }}>
                <DropSelect label="Campagne" value={filters.campagne_selected} onChange={onCampagne} options={campOpts} placeholder="Toutes les campagnes" minWidth={210} />
                <DropSelect label="Ville" value={filters.ville} onChange={upd("ville")} options={villes} placeholder="Toutes les villes" minWidth={155} />
                <DropSelect label="Opérateur" value={filters.operateur} onChange={upd("operateur")} options={operateurs} placeholder="Tous les opérateurs" minWidth={165} />
                <DropSelect label="Technologie" value={filters.service} onChange={upd("service")} options={services.length ? services : ["2G", "3G", "4G", "5G"]} placeholder="Toutes technos" minWidth={130} />
                <DropSelect label="Qualité auditive" value={filters.qa_categorie} onChange={upd("qa_categorie")} options={["Parfaite", "Acceptable", "Médiocre", "Mauvaise"]} placeholder="Toutes qualités" minWidth={160} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "9px 16px", borderRight: `1px solid ${C.line}`, justifyContent: "center" }}>
                    <span style={labelStyle}>Période</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="date" value={filters.date_debut} onChange={e => setFilters(f => ({ ...f, date_debut: e.target.value, campagne_selected: "" }))} className="qos-date" style={{ padding: "4px 6px", borderRadius: 6, border: `1px solid ${C.line}`, fontSize: 12, color: C.navy, fontFamily: FONT }} />
                        <span style={{ color: C.muted, fontSize: 12 }}>→</span>
                        <input type="date" value={filters.date_fin} onChange={e => setFilters(f => ({ ...f, date_fin: e.target.value, campagne_selected: "" }))} className="qos-date" style={{ padding: "4px 6px", borderRadius: 6, border: `1px solid ${C.line}`, fontSize: 12, color: C.navy, fontFamily: FONT }} />
                    </div>
                </div>

            </div>
            {n > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.muted }}>Actifs</span>
                    {chips.map(ch => (
                        <button key={ch.key} onClick={ch.clear} className="qos-chip" style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.navy, background: "#EEF2F8", border: `1px solid #D5DFEC`, borderRadius: 16, padding: "5px 8px 5px 12px", cursor: "pointer" }}>
                            <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#fff", background: ch.color, borderRadius: 4, padding: "2px 6px" }}>{ch.tag}</span>
                            <span>{ch.label}</span>
                            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: "#CBD6E5", color: C.navy, fontSize: 12 }}>×</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Cellules
// ─────────────────────────────────────────────────────────
const th = (align = "center") => ({ padding: "11px 14px", color: "#FFF", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: align });
const Num = ({ children, color }) => <td style={{ padding: "13px 10px", textAlign: "right", fontFamily: MONO, fontSize: 13, color: color || C.navy }}>{children}</td>;
const Pct = ({ val, good, warn }) => <td style={{ padding: "13px 10px", textAlign: "right", fontFamily: MONO, fontSize: 13, fontWeight: good || warn ? 700 : 500, color: good ? C.green : warn ? C.orange : C.navy }}>{fmtPct(val)}</td>;
const Leg = ({ c, t }) => <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{t}</span>;

function SigneBadge({ signe }) {
    const color = SIGNE_COLOR[signe] || C.grey;
    const active = { "+++": [1, 2, 3, 4], "++": [1, 2, 3], "+": [1, 2], "=": [2, 3], "---": [1], "--": [1, 2], "-": [1, 2, 3] }[signe] || [2, 3];
    return (
        <div title={SIGNE_LABEL[signe] || ""} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 10px", borderRadius: 4, background: `${color}14`, border: `1px solid ${color}44` }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 14 }}>
                {[1, 2, 3, 4].map(b => <div key={b} style={{ width: 3, height: 3 + b * 2.5, borderRadius: 1, background: active.includes(b) ? color : C.line }} />)}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color, minWidth: 26 }}>{signe || "—"}</span>
        </div>
    );
}

// Barème des signes — légende partagée
function BaremeSignes() {
    return (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>Barème</span>
            {["+++", "++", "+", "=", "-", "--", "---"].map(s => (
                <span key={s} title={SIGNE_LABEL[s]} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: C.grey }}>
                    <span style={{ fontFamily: MONO, fontWeight: 700, color: SIGNE_COLOR[s], fontSize: 12.5 }}>{s}</span>
                    <span style={{ color: C.muted }}>{SIGNE_LABEL[s]}</span>
                </span>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Tableau — Taux de réussite par opérateur
// ─────────────────────────────────────────────────────────
function TableauReussite({ data, loading, error }) {
    const rows = data?.results || [];
    if (loading) return <SkeletonTable rows={3} cols={7} />;
    if (error) return <ApiError error={error} />;
    if (!rows.length) return <Empty msg="Aucun résultat. Lancez le pipeline Apache Hop après collecte." />;
    const tmr = rows[0]?.tmr ?? null;
    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr style={{ background: C.navy }}>
                        <th style={th("left")}>Opérateur</th>
                        {["Mesures", "TE %", "TC %", "TR %", "TMR %"].map(h => <th key={h} style={th("right")}>{h}</th>)}
                        <th style={th("center")}>Signe</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.operateur} className="qos-row" style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                            <td style={{ padding: "13px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                    <span style={{ width: 11, height: 11, borderRadius: "50%", background: opColor(r.operateur), flexShrink: 0 }} />
                                    <span style={{ fontWeight: 600, fontSize: 13.5, color: C.navy }}>{r.operateur}</span>
                                </div>
                            </td>
                            <Num color="#475569">{fmtInt(r.total_mesures)}</Num>
                            <Pct val={r.te_pct} warn={r.te_pct > 5} />
                            <Pct val={r.tc_pct} warn={r.tc_pct > 5} />
                            <Pct val={r.tr_pct} good={r.tr_pct >= 95} />
                            <Num color={C.grey}>{fmtPct(r.tmr ?? 0, 1)}</Num>
                            <td style={{ textAlign: "center", padding: "13px 10px" }}><SigneBadge signe={r.signe} /></td>
                        </tr>
                    ))}
                </tbody>
                {tmr !== null && (
                    <tfoot>
                        <tr style={{ background: C.navyMid }}>
                            <td colSpan={5} style={{ padding: "11px 14px", fontSize: 12, color: "#9FB0C9", fontStyle: "italic" }}>TMR = moyenne des TR % des {rows.length} opérateurs</td>
                            <td colSpan={2} style={{ padding: "11px 14px", textAlign: "center", fontFamily: MONO, fontWeight: 700, fontSize: 15, color: "#FFF" }}>{fmtPct(tmr, 1)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
            <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap", fontSize: 11, fontWeight: 600, color: C.grey }}>
                <Leg c={C.green} t="TR — réussite" /><Leg c={C.amber} t="TC — coupure" /><Leg c={C.orange} t="TE — échec" />
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Résultats de la campagne par ville (matrice signes)
// ─────────────────────────────────────────────────────────
function SectionResultatsCampagne({ data, loading, error, campagneSelectionnee, campagnes }) {
    const rows = data?.results || [];
    const infoCampagne = campagnes.find(c => c.date_mesure === campagneSelectionnee);
    const numeroCampagne = campagnes.findIndex(c => c.date_mesure === campagneSelectionnee) + 1;
    const villes = [...new Set(rows.map(r => r.site_emission))].filter(Boolean).sort();
    const operateurs = [...new Set(rows.map(r => r.operateur))].filter(Boolean).sort();
    const idx = {};
    rows.forEach(r => { if (!idx[r.site_emission]) idx[r.site_emission] = {}; idx[r.site_emission][r.operateur] = r; });

    const tauxMoyenVille = (ville) => {
        const lignes = operateurs.map(op => idx[ville]?.[op]).filter(Boolean);
        if (!lignes.length) return null;
        return Math.round(lignes.reduce((s, r) => s + (r.tr_pct || 0), 0) / lignes.length * 100) / 100;
    };
    const tmrGlobal = Number(data?.tmr ?? 0).toFixed(1);
    const calcSigne = (tr_pct, tmr) => {
        if (!tmr) return "=";
        const ratio = tr_pct / tmr;
        if (ratio > 1.05) return "+++"; if (ratio > 1.02) return "++"; if (ratio > 1.01) return "+";
        if (ratio >= 0.99) return "="; if (ratio >= 0.98) return "-"; if (ratio >= 0.95) return "--"; return "---";
    };
    const signeStyle = (signe) => ({ color: SIGNE_COLOR[signe] || C.grey, fontWeight: 700, fontFamily: MONO, fontSize: 15, letterSpacing: "0.04em" });

    const campLabel = infoCampagne ? `Campagne ${numeroCampagne} — ${infoCampagne.date_mesure} · ${fmtInt(infoCampagne.nb_mesures)} mesures` : "Toutes les campagnes";
    const tmrChip = !loading && !error && rows.length > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: C.greyLight, border: `1px solid ${C.line}`, borderRadius: 8, padding: "6px 12px" }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>TMR global</span>
            <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: tmrGlobal >= 95 ? C.green : C.navy }}>{fmtPct(tmrGlobal)}</span>
        </div>
    ) : null;

    return (
        <section style={{ marginBottom: 20 }}>
            <Card style={{ padding: "20px 22px" }}>
                <SectionTitle sub={`${campLabel} · positionnement comparatif des opérateurs par ville`} right={tmrChip}>Résultats de la campagne de mesures</SectionTitle>
                {loading ? <SkeletonTable rows={5} cols={5} />
                    : error ? <ApiError error={error} />
                        : rows.length === 0 ? <Empty msg="Aucune mesure disponible pour cette sélection." />
                            : (
                                <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 10 }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 200 + 140 + operateurs.length * 120 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ background: "#F5C718", color: C.navy, padding: "15px 20px", textAlign: "left", fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", minWidth: 160, borderBottom: `2px solid ${C.line}` }}>Villes</th>
                                                <th style={{ background: C.navyLight, color: "#FFF", padding: "15px 14px", textAlign: "center", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.4, minWidth: 110, borderBottom: `2px solid ${C.line}`, borderLeft: "2px solid rgba(255,255,255,0.2)" }}>Taux moyen<br />de réussite</th>
                                                {operateurs.map(op => (
                                                    <th key={op} style={{ background: opColor(op), color: "#FFF", padding: "15px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, letterSpacing: "0.02em", minWidth: 100, borderBottom: `2px solid ${C.line}`, borderLeft: "2px solid rgba(255,255,255,0.3)" }}>{op}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {villes.map((ville, i) => {
                                                const tmrVille = tauxMoyenVille(ville);
                                                return (
                                                    <tr key={ville} className="qos-row" style={{ background: i % 2 === 0 ? "#F0FAFA" : C.card, borderBottom: `1px solid ${C.lineSoft}` }}>
                                                        <td style={{ padding: "13px 20px", fontSize: 13, color: C.navy, fontWeight: 500, borderRight: `1px solid ${C.line}` }}>{ville}</td>
                                                        <td style={{ padding: "13px 10px", textAlign: "center", fontFamily: MONO, fontSize: 13, fontWeight: 700, borderRight: `1px solid ${C.line}`, color: tmrVille != null && tmrVille >= 95 ? C.green : tmrVille != null && tmrVille < 50 ? C.red : C.navy }}>{tmrVille != null ? fmtPct(tmrVille) : "—"}</td>
                                                        {operateurs.map(op => {
                                                            const cell = idx[ville]?.[op];
                                                            const signe = cell ? calcSigne(cell.tr_pct || 0, tmrGlobal) : null;
                                                            return (
                                                                <td key={op} title={cell ? `${op} · ${ville} · TR ${fmtPct(cell.tr_pct || 0)}` : ""} style={{ padding: "13px 10px", textAlign: "center", borderLeft: `1px solid ${C.line}44` }}>
                                                                    {signe ? <span style={signeStyle(signe)}>{signe}</span> : <span style={{ color: C.line, fontSize: 16 }}>—</span>}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                            <tr style={{ background: C.navyLight, borderTop: `2px solid ${C.navy}` }}>
                                                <td style={{ padding: "13px 20px", fontWeight: 700, fontSize: 13, color: "#FFF", borderRight: `1px solid rgba(255,255,255,0.2)` }}>ENSEMBLE DES SITES</td>
                                                <td style={{ padding: "13px 10px", textAlign: "center", fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#A8F0C8", borderRight: `1px solid rgba(255,255,255,0.2)` }}>{fmtPct(tmrGlobal)}</td>
                                                {operateurs.map(op => {
                                                    const lignesOp = rows.filter(r => r.operateur === op);
                                                    const total = lignesOp.reduce((s, r) => s + (r.total_mesures || 0), 0);
                                                    const nbTR = lignesOp.reduce((s, r) => s + (r.nb_TR || 0), 0);
                                                    const trPct = total ? Math.round(nbTR / total * 10000) / 100 : 0;
                                                    const signe = calcSigne(trPct, tmrGlobal);
                                                    return (<td key={op} style={{ padding: "13px 10px", textAlign: "center", borderLeft: `1px solid rgba(255,255,255,0.15)` }}><span style={{ ...signeStyle(signe), color: "#FFF", fontSize: 16 }}>{signe}</span></td>);
                                                })}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 9 }}>
                    <BaremeSignes />
                    <div style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic" }}>Signes attribués par comparaison au TMR global ({fmtPct(tmrGlobal)}) selon le barème réglementaire · TE : appel ≤ 5 s · TC : coupure entre 5 s et 120 s · TR : appel maintenu ≥ 120 s (2 min).</div>
                </div>
            </Card>
        </section>
    );
}

// ─────────────────────────────────────────────────────────
// Tableau — Qualité auditive × Opérateur × Ville
// ─────────────────────────────────────────────────────────
function TableauQualiteVille({ data, loading, error }) {
    const operateurs = data?.operateurs || [];
    const villes = data?.villes || [];
    const table = data?.table || {};
    if (loading) return <SkeletonTable rows={5} cols={6} />;
    if (error) return <ApiError error={error} />;
    if (!operateurs.length) return <Empty msg="Aucune donnée de qualité auditive disponible." />;
    const COLS = [
        { key: "parfaite", color: QA_COLOR.parfaite, label: "Parfaite" },
        { key: "acceptable", color: QA_COLOR.acceptable, label: "Acceptable" },
        { key: "mediocre", color: QA_COLOR.mediocre, label: "Médiocre" },
        { key: "mauvaise", color: QA_COLOR.mauvaise, label: "Mauvaise" },
    ];
    return (
        <div style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", gap: 18, marginBottom: 14, flexWrap: "wrap", fontSize: 11.5, fontWeight: 600, color: C.grey }}>
                {COLS.map(c => <Leg key={c.key} c={c.color} t={c.label} />)}
            </div>
            <table style={{ borderCollapse: "collapse", minWidth: 360 + operateurs.length * 210 }}>
                <thead>
                    <tr>
                        <th style={{ width: 150, background: C.greyLight, borderBottom: `2px solid ${C.line}`, borderRight: `1px solid ${C.line}` }} />
                        {operateurs.map(op => (
                            <th key={op} colSpan={4} style={{ background: opColor(op), color: "#FFF", fontSize: 13, fontWeight: 700, textTransform: "uppercase", padding: "10px 8px", textAlign: "center", borderLeft: "2px solid #FFF", borderBottom: `2px solid ${C.line}` }}>{op}</th>
                        ))}
                    </tr>
                    <tr style={{ background: C.greyLight }}>
                        <th style={{ borderBottom: `1px solid ${C.line}`, borderRight: `1px solid ${C.line}` }} />
                        {operateurs.map(op => COLS.map(c => (
                            <th key={`${op}-${c.key}`} style={{ padding: "6px 4px", borderLeft: c.key === "parfaite" ? "2px solid #FFF" : "none", borderBottom: `1px solid ${C.line}` }}>
                                <div style={{ width: 14, height: 7, background: c.color, margin: "0 auto", borderRadius: 2 }} />
                            </th>
                        )))}
                    </tr>
                </thead>
                <tbody>
                    {villes.map((ville, i) => {
                        const isTotal = ville === "Ensemble des sites";
                        return (
                            <tr key={ville} className={isTotal ? "" : "qos-row"} style={{ background: isTotal ? C.navyLight : i % 2 ? C.greyLight : C.card, fontWeight: isTotal ? 700 : 400, borderTop: isTotal ? `2px solid ${C.navy}` : undefined }}>
                                <td style={{ padding: "11px 14px", fontSize: 13, color: isTotal ? "#FFF" : C.navy, whiteSpace: "nowrap", borderRight: `1px solid ${C.line}`, fontWeight: isTotal ? 700 : 500 }}>{ville}</td>
                                {operateurs.map(op => {
                                    const cell = table[ville]?.[op];
                                    return COLS.map(c => (
                                        <td key={`${ville}-${op}-${c.key}`} style={{ padding: "11px 6px", textAlign: "center", fontFamily: MONO, fontSize: 12, color: isTotal ? "#FFF" : cell ? C.navy : C.muted, borderLeft: c.key === "parfaite" ? "2px solid rgba(255,255,255,0.18)" : "none" }}>
                                            {cell ? `${cell[c.key].toFixed(1).replace(".", ",")}\u00A0%` : "—"}
                                        </td>
                                    ));
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Tableau — Résultats détaillés par ville
// ─────────────────────────────────────────────────────────
function TableauParVille({ data, loading, error }) {
    const rows = data?.results || [];
    if (loading) return <SkeletonTable rows={4} cols={6} />;
    if (error) return <ApiError error={error} />;
    if (!rows.length) return <Empty msg="Aucune donnée par ville disponible." />;
    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr style={{ background: C.navyMid }}>
                        <th style={th("left")}>Ville</th><th style={th("left")}>Opérateur</th>
                        {["Mesures", "TE %", "TC %", "TR %"].map(h => <th key={h} style={th("right")}>{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={`${r.site_emission}-${r.operateur}-${i}`} className="qos-row" style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                            <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 13, color: C.navy }}>{r.site_emission}</td>
                            <td style={{ padding: "12px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: opColor(r.operateur) }} />
                                    <span style={{ fontSize: 13, color: C.navy }}>{r.operateur}</span>
                                </div>
                            </td>
                            <Num color="#475569">{fmtInt(r.total_mesures)}</Num>
                            <Pct val={r.te_pct} warn={r.te_pct > 5} />
                            <Pct val={r.tc_pct} />
                            <Pct val={r.tr_pct} good={r.tr_pct >= 95} warn={r.tr_pct < 50} />
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Carte Leaflet
// ─────────────────────────────────────────────────────────
function CarteMesures({ data, loading, error }) {
    const points = data?.results || [];
    const center = <div style={{ ...boxState, border: "none", background: "transparent", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} />;
    return (
        <div style={{ border: `1px solid ${C.line}`, borderRadius: 10, overflow: "hidden", height: 420, position: "relative", background: C.greyLight }}>
            {loading ? <div style={{ height: "100%", padding: 12 }}><Skeleton h="100%" r={8} /></div>
                : error ? <div style={{ padding: 16 }}><ApiError error={error} /></div>
                    : !points.length ? <div style={{ ...boxState, border: "none", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Aucune mesure géolocalisée pour cette sélection.</div>
                        : (
                            <MapContainer center={[31.7917, -7.0926]} zoom={6} style={{ height: "100%", width: "100%" }}>
                                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                {points.map((p, i) => (
                                    <CircleMarker key={i} center={[p.latitude, p.longitude]} radius={8} pathOptions={{ color: opColor(p.operateur), fillColor: opColor(p.operateur), fillOpacity: 0.8, weight: 2 }}>
                                        <Popup>
                                            <div style={{ fontFamily: FONT, fontSize: 13, lineHeight: 1.6 }}>
                                                <strong style={{ color: opColor(p.operateur) }}>{p.operateur}</strong><br />
                                                Mesure n°{p.mesure} — {p.site_emission}<br />
                                                Durée : {p.duree}s · Signal : {p.signal_strength} dBm<br />
                                                Qualité : {p.quality_pesq || "—"}
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                        )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Panneau Machine Learning
// ─────────────────────────────────────────────────────────
function PanneauML({ anomaliesData, classifData, loadingA, errorA, loadingC, errorC }) {
    const anomalies = anomaliesData?.results || [];
    const nbAnomalies = anomalies.filter(a => a.est_anomalie === 1).length;
    const pctAnom = anomalies.length ? (nbAnomalies / anomalies.length) * 100 : 0;
    const classif = classifData?.result;
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card style={{ padding: 22 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: C.grey, marginBottom: 14 }}>Détection d'anomalies · Isolation Forest</div>
                {loadingA ? <Skeleton h={60} /> : errorA ? <ApiError error={errorA} /> : anomalies.length === 0 ? <Empty msg="Aucune mesure analysée." /> : (
                    <>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                            <span style={{ fontFamily: MONO, fontSize: 30, fontWeight: 700, color: nbAnomalies > 0 ? C.red : C.green }}>{fmtInt(nbAnomalies)}</span>
                            <span style={{ fontSize: 13, color: C.grey }}>/ {fmtInt(anomalies.length)} mesures · {fmtPct(pctAnom, 1)}</span>
                        </div>
                        <div style={{ height: 8, background: C.lineSoft, borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.min(100, pctAnom)}%`, background: C.red, borderRadius: 4 }} />
                        </div>
                        <div style={{ fontSize: 12, color: C.grey, marginTop: 8 }}>mesures signalées comme atypiques</div>
                    </>
                )}
            </Card>
            <Card style={{ padding: 22 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: C.grey, marginBottom: 14 }}>Classification qualité · Random Forest</div>
                {loadingC ? <Skeleton h={60} /> : errorC ? <ApiError error={errorC} /> : !classif?.accuracy ? (
                    <div style={{ fontSize: 13, color: C.grey, lineHeight: 1.6 }}>{classif?.message || "Données insuffisantes pour l'entraînement."}</div>
                ) : (
                    <>
                        <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
                            {[{ l: "Exactitude", v: classif.accuracy }, { l: "F1 moyen", v: classif.f1_moyen }, { l: "Recall", v: classif.recall_moyen }, { l: "Précision", v: classif.precision_moyenne }].map(m => (
                                <div key={m.l}>
                                    <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: m.v >= 0.8 ? C.green : m.v >= 0.6 ? C.amber : C.red }}>{fmtPct(m.v * 100, 1)}</div>
                                    <div style={{ fontSize: 11, color: C.grey, marginTop: 3 }}>{m.l}</div>
                                </div>
                            ))}
                        </div>
                        {classif.feature_importances && (
                            <div style={{ marginTop: 18 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: C.grey, marginBottom: 10 }}>Importance des variables</div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {Object.entries(classif.feature_importances).sort((a, b) => b[1] - a[1]).map(([f, imp]) => (
                                        <div key={f}>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.navy, marginBottom: 4 }}>
                                                <span>{f}</span><span style={{ fontFamily: MONO, fontWeight: 600 }}>{fmtPct(imp * 100, 1)}</span>
                                            </div>
                                            <div style={{ height: 6, background: C.line, borderRadius: 3 }}>
                                                <div style={{ height: "100%", width: `${imp * 100}%`, background: C.navy, borderRadius: 3 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// KPI
// ─────────────────────────────────────────────────────────
function KpiCard({ label, value, suffix, foot, accent, loading }) {
    return (
        <Card style={{ overflow: "hidden", flex: 1, minWidth: 150 }}>
            <div style={{ height: 3, background: accent }} />
            <div style={{ padding: "15px 18px 16px" }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: C.grey }}>{label}</div>
                {loading ? <div style={{ marginTop: 12 }}><Skeleton w="60%" h={22} /></div> : (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 11 }}>
                        <span style={{ fontFamily: MONO, fontSize: 25, fontWeight: 700, color: C.navy }}>{value}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: C.muted }}>{suffix}</span>
                    </div>
                )}
                {foot && <div style={{ fontSize: 11.5, fontWeight: 500, color: C.muted, marginTop: 6 }}>{foot}</div>}
            </div>
        </Card>
    );
}

// Styles globaux (keyframes, hover, scrollbar, inputs)
function GlobalStyle() {
    return (
        <style>{`
            @keyframes qosShimmer { 0% { background-position: 100% 0 } 100% { background-position: 0 0 } }
            .qos-row { transition: background .12s ease; }
            .qos-row:hover { background: #F3F7FC !important; }
            .qos-opt:hover { background: #F1F5FA !important; }
            .qos-btn-ghost:hover { background: #FBEEF1 !important; }
            .qos-chip:hover { background: #E2E9F4 !important; }
            .qos-toggle:hover { background: #F3F7FC; }
            .qos-date { outline: none; }
            .qos-date:focus { border-color: ${C.navy} !important; }
            ::-webkit-scrollbar { width: 9px; height: 9px; }
            ::-webkit-scrollbar-thumb { background: #CFD8E5; border-radius: 6px; }
            ::-webkit-scrollbar-thumb:hover { background: #B7C4D6; }
        `}</style>
    );
}

// ─────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────
export default function QoSDashboard() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const isNarrow = useMediaQuery("(max-width: 1080px)");

    const hasActive = useMemo(() =>
        Object.entries(filters).some(([k, v]) => k !== "campagne_selected" && v !== "" && v !== false) || !!filters.campagne_selected,
        [filters]);

    const reussiteStatic = useApi("/api/kpi/reussite");
    const villesApi = useApi("/api/villes");
    const operateursApi = useApi("/api/operateurs");
    const servicesApi = useApi("/api/services");
    const campagnesApi = useApi("/api/campagnes");

    const reussiteDyn = useDynamic("/api/kpi/filtre", filters);
    const qaVilleDyn = useDynamic("/api/kpi/qualite_par_ville", filters);
    const parVilleDyn = useDynamic("/api/kpi/par_ville", filters);
    const carteDyn = useDynamic("/api/carte/mesures", filters);
    const anomaliesDyn = useDynamic("/api/ml/anomalies", filters);
    const classifDyn = useDynamic("/api/ml/classification", filters);

    const reussiteData = hasActive ? reussiteDyn : reussiteStatic;

    const campagnes = campagnesApi.data?.campagnes || [];
    const villes = villesApi.data?.villes || [];
    const operateurs = operateursApi.data?.operateurs || [];
    const services = servicesApi.data?.services || [];

    const rows = reussiteData.data?.results || [];
    const totalMesures = rows.reduce((s, r) => s + (r.total_mesures || 0), 0);
    const tmr = rows[0]?.tmr ?? null;
    const meilleur = [...rows].sort((a, b) => (b.tr_pct || 0) - (a.tr_pct || 0))[0];
    const nbAnom = (anomaliesDyn.data?.results || []).filter(a => a.est_anomalie === 1).length;
    const villeActive = filters.ville;
    const kpiLoading = reussiteData.loading;

    return (
        <div style={{ fontFamily: FONT, background: C.paper, minHeight: "100vh", color: C.navy }}>
            <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap" rel="stylesheet" />
            <GlobalStyle />

            {/* En-tête institutionnel */}
            <header style={{ background: C.navy, borderBottom: `4px solid ${C.red}`, position: "sticky", top: 0, zIndex: 500 }}>
                <div style={{ maxWidth: 1440, margin: "0 auto", padding: "16px 34px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                        <div style={{ width: 46, height: 46, borderRadius: 9, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 3, padding: "9px 0" }}>
                            {[[11, "#5B8FCF"], [17, "#5B8FCF"], [23, "#9FBEE3"], [28, C.red]].map(([h, c], i) => <span key={i} style={{ width: 5, height: h, background: c, borderRadius: 1.5 }} />)}
                        </div>
                        <div style={{ lineHeight: 1.2 }}>
                            <div style={{ fontSize: 18.5, fontWeight: 700, letterSpacing: "0.04em", color: "#FFF" }}>PLATEFORME QoS 4G/5G — MAROC</div>
                            <div style={{ fontSize: 12, color: "#9FB0C9", fontWeight: 500, marginTop: 2 }}>Évaluation de la qualité de service vocale </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

                        {hasActive
                            ? <span style={{ fontSize: 11.5, fontWeight: 600, color: "#FFD9DE", background: "#5C1828", padding: "6px 12px", borderRadius: 6 }}>VUE FILTRÉE · {fmtInt(totalMesures)} mesure{totalMesures > 1 ? "s" : ""}</span>
                            : <span style={{ fontSize: 11.5, fontWeight: 600, color: "#B9F0D2", background: "#123C2A", padding: "6px 12px", borderRadius: 6 }}>VUE NATIONALE</span>}
                    </div>
                </div>
            </header>

            <main style={{ maxWidth: 1440, margin: "0 auto", padding: "24px 34px 44px" }}>
                {/* KPI */}
                <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
                    <KpiCard loading={kpiLoading} label="Mesures collectées" value={fmtInt(totalMesures)} suffix="" foot={hasActive ? "vue filtrée" : "toutes campagnes"} accent={C.navy} />
                    <KpiCard loading={kpiLoading} label="TMR" value={tmr !== null ? Number(tmr).toFixed(1).replace(".", ",") : "—"} suffix="%" foot="taux moyen de réussite" accent={C.green} />
                    <KpiCard loading={kpiLoading} label="Meilleur opérateur" value={meilleur?.operateur || "—"} suffix="" foot={meilleur ? `${Number(meilleur.tr_pct || 0).toFixed(1).replace(".", ",")} % TR` : ""} accent={opColor(meilleur?.operateur)} />
                    <KpiCard loading={anomaliesDyn.loading} label="Anomalies ML" value={fmtInt(nbAnom)} suffix="" foot="Isolation Forest" accent={C.red} />
                    <KpiCard loading={campagnesApi.loading} label="Campagnes" value={fmtInt(campagnes.length)} suffix="" foot="collectes terrain" accent={C.navyLight} />
                </div>

                {/* Filtres */}
                <UnifiedFilterBar filters={filters} setFilters={setFilters} campagnes={campagnes} villes={villes} operateurs={operateurs} services={services} />

                {/* Grille principale */}
                <div style={{ display: "grid", gridTemplateColumns: isNarrow ? "1fr" : "minmax(0,1.9fr) minmax(320px,1fr)", gap: 20, alignItems: "start" }}>
                    <div style={{ minWidth: 0 }}>
                        <Section title={villeActive ? `Taux de réussite — ${villeActive}` : "Taux de réussite par opérateur"} sub="Indicateurs TE / TC / TR et signe comparatif">
                            <TableauReussite data={reussiteData.data} loading={reussiteData.loading} error={reussiteData.error} />
                        </Section>

                        <SectionResultatsCampagne data={parVilleDyn.data} loading={parVilleDyn.loading} error={parVilleDyn.error} campagneSelectionnee={filters.campagne_selected} campagnes={campagnes} />

                        <Section title="Qualité auditive par opérateur et par ville" sub="Répartition PESQ : parfaite / acceptable / médiocre / mauvaise">
                            <TableauQualiteVille data={qaVilleDyn.data} loading={qaVilleDyn.loading} error={qaVilleDyn.error} />
                        </Section>

                        {villeActive ? (
                            <Section title={`Résultats détaillés — ${villeActive}`} sub="Détail par opérateur du site sélectionné"
                                right={<button onClick={() => setFilters(f => ({ ...f, ville: "" }))} className="qos-chip" style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.navy, background: C.greyLight, border: `1px solid ${C.line}`, borderRadius: 8, padding: "7px 12px", cursor: "pointer" }}>← Vue d'ensemble</button>}>
                                <TableauParVille data={parVilleDyn.data} loading={parVilleDyn.loading} error={parVilleDyn.error} />
                            </Section>
                        ) : (
                            <Section title="Résultats détaillés par ville" sub="Sélectionnez une ville dans les filtres pour recentrer l'analyse">
                                <TableauParVille data={parVilleDyn.data} loading={parVilleDyn.loading} error={parVilleDyn.error} />
                            </Section>
                        )}

                        <Section title="Machine Learning — anomalies et classification" sub="Modèles scikit-learn appliqués aux mesures filtrées">
                            <PanneauML anomaliesData={anomaliesDyn.data} classifData={classifDyn.data} loadingA={anomaliesDyn.loading} errorA={anomaliesDyn.error} loadingC={classifDyn.loading} errorC={classifDyn.error} />
                        </Section>
                    </div>

                    {/* Carte (sticky droite) */}
                    <aside style={{ position: isNarrow ? "static" : "sticky", top: 90 }}>
                        <Card style={{ padding: "18px 18px 16px" }}>
                            <SectionTitle right={<span style={{ fontFamily: MONO, fontSize: 10.5, color: C.muted }}>{filters.service || "toutes technos"}</span>}>Cartographie des mesures</SectionTitle>
                            <CarteMesures data={carteDyn.data} loading={carteDyn.loading} error={carteDyn.error} />
                            <div style={{ display: "flex", gap: 14, marginTop: 13, flexWrap: "wrap", fontSize: 11, fontWeight: 600, color: "#475569" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i style={{ width: 9, height: 9, borderRadius: "50%", background: "#0066B3" }} />Opérateur 2</span>
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i style={{ width: 9, height: 9, borderRadius: "50%", background: "#F36A21" }} />Opérateur 1</span>
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i style={{ width: 9, height: 9, borderRadius: "50%", background: "#7A1FA2" }} />Opérateur 3</span>
                            </div>
                            <p style={{ margin: "12px 0 0", fontSize: 10.5, lineHeight: 1.5, color: C.muted }}>Couleur du marqueur = opérateur mesuré · cliquez un point pour le détail de la mesure (durée, signal, PESQ).</p>
                        </Card>
                    </aside>
                </div>

                <footer style={{ marginTop: 28, paddingTop: 18, borderTop: `1px solid ${C.line}`, textAlign: "center", fontSize: 11, color: C.muted, lineHeight: 1.6 }}>

                    Plateforme d'évaluation de la qualité de service vocale 4G/5G · Collecte terrain Android · ETL Apache Hop · Machine Learning scikit-learn · API Flask · © 2026
                    {hasActive && " · vue filtrée recalculée dynamiquement"}
                </footer>
            </main>
        </div>
    );
}
